import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Form,
  TvInput,
  TvAlert,
  Spinner,
  TvButton,
  TvIconButton,
  TvDiv,
  TvLabel,
  TvSmall,
} from "@trustvid/design-system";
import { Authlogin } from "../../../services/auth";
import { verifyMFA } from "../../../services/mfa";
import { toast } from "react-toastify";
import { buildScopedPath } from "../../../utils/account-context";
import { clearUserSession, persistUserData } from "../../../utils/auth-helpers";
import { clearClientSessionCaches } from "../../../core/cache/clearClientSessionCaches";
import { beginGoogleSso, isGoogleSsoUiEnabled } from "../../../utils/googleSso";
import AuthSplitLayout from "./AuthSplitLayout";
import AuthGoogleSsoButton from "./AuthGoogleSsoButton";
import AuthFormDivider from "./AuthFormDivider";
import AuthContinueWithEmailButton from "./AuthContinueWithEmailButton";
import { AUTH_FIELD_LABEL_CLASS, AUTH_GOOGLE_SSO_COPY, LOGIN_PAGE_COPY } from "./loginPageContent";

const validationSchema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const Login = () => {
  document.title = "Login";

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [emailAutocompleteEnabled, setEmailAutocompleteEnabled] = useState(false);
  const [passwordAutocompleteEnabled, setPasswordAutocompleteEnabled] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  //const navigate = useNavigate();
  const location = useLocation();
  const googleSsoEnabled = isGoogleSsoUiEnabled();

  useEffect(() => {
    if (location.state?.googleMfaRequired) {
      setMfaRequired(true);
      toast.info("MFA required. Enter your authentication code.");
    }
  }, [location.state]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");
      try {
        const response = await Authlogin(values);

        if (response?.message === "MFA required.") {
          // Drop any stale prior-tenant session before writing the new one, otherwise
          // leftover `accountId`/caches from a previous account can drive requests to
          // the wrong tenant and trigger 403s from authorizeAccountAccess.
          clearUserSession();
          clearClientSessionCaches();
          localStorage.setItem("token", response?.token);
          localStorage.setItem("accountId", response?.user?.account?.id);
          localStorage.setItem("userType", "user");
          setMfaRequired(true);
          toast.info("MFA required. Enter your authentication code.");
        } else if (response?.error) {
          setError(response.error);
        } else {
          const accountId = response?.user?.account?.id;
          clearUserSession();
          clearClientSessionCaches();
          localStorage.setItem("token", response?.token);
          localStorage.setItem("accountId", accountId);
          localStorage.setItem("userType", "user");
          persistUserData(response?.user);
          toast.success("Login successful!");
          // Hard navigation on session boundary: forces AccountBrandingProvider and every
          // other App-root provider to remount, so lingering React state (availableAccounts,
          // tenantBootstrap, in-memory caches keyed off the previous tenant) cannot cause
          // the app to redirect the new session back to the old account's URL and fire
          // /accounts/<oldId>/... requests that 403 against the new JWT.
          window.location.assign(buildScopedPath(accountId, "/dashboard"));
        }
      } catch (err) {
        console.error("Login failed", err);
        toast.error("Unable to login, Please try again!");
        setError("Something went wrong, please try again later.");
        formik.resetForm();
      } finally {
        setLoading(false);
      }
    },
  });

  const handleMfaSubmit = async () => {
    if (!mfaCode) {
      toast.error("Please enter your MFA code.");
      return;
    }

    setLoading(true);
    try {
      const response = await verifyMFA(mfaCode);

      if (response?.token) {
        const accountId = response?.user?.account?.id;
        // Same defense-in-depth as the primary login path — never leak prior tenant state.
        clearUserSession();
        clearClientSessionCaches();
        localStorage.setItem("token", response?.token);
        localStorage.setItem("accountId", accountId);
        localStorage.setItem("userType", "user");
        persistUserData(response?.user);
        toast.success("Login successful!");
        // Hard navigation on session boundary — see comment in the primary login path.
        window.location.assign(buildScopedPath(accountId, "/dashboard"));
      } else {
        toast.error("Invalid MFA code. Please try again.");
        setError("Invalid MFA code.");
      }
    } catch (err) {
      toast.error("MFA verification failed. Try again.");
      setError("MFA verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const hasCredentials =
    formik.values.email.trim().length > 0 && formik.values.password.length > 0;

  return (
    <AuthSplitLayout
      pageTestId="login-page"
      cardTestId="login-form-card"
      brandTestId="login-platform-brand"
      showCardHeading={false}
      cardTitle={LOGIN_PAGE_COPY.pageTitle}
      welcomeHeading={LOGIN_PAGE_COPY.pageTitle}
      welcomeHeadingTestId="login-card-title"
      cardSubtitle={LOGIN_PAGE_COPY.pageSubtitle}
      featurePanelHeading={LOGIN_PAGE_COPY.featurePanelHeading}
    >
      {!mfaRequired ? (
        <>
          {/* TVD-1292: Google OpenID SSO — shown first so users can sign in without the email form. */}
          <AuthGoogleSsoButton
            testId="login-google-sso"
            enabled={googleSsoEnabled}
            onClick={() => {
              if (!googleSsoEnabled) {
                toast.info("Google sign-in is not enabled yet.");
                return;
              }
              beginGoogleSso({ intent: "login" });
            }}
          />
          <AuthFormDivider>{AUTH_GOOGLE_SSO_COPY.orDivider}</AuthFormDivider>
          {showEmailForm ? (
          <Form onSubmit={formik.handleSubmit} data-testid="login-form" className="tv-login-page__form" autoComplete="off">
          {error ? (
            <TvAlert color="danger" className="mb-3" data-testid="login-error-alert" fade={false}>
              {error}
            </TvAlert>
          ) : null}

          <TvDiv className="mb-3">
            <TvLabel htmlFor="login-email" className={AUTH_FIELD_LABEL_CLASS}>
              {LOGIN_PAGE_COPY.emailLabel}
            </TvLabel>
            <TvInput
              id="login-email"
              name="email"
              type="email"
              autoComplete={emailAutocompleteEnabled ? "email" : "off"}
              readOnly={!emailAutocompleteEnabled}
              placeholder={LOGIN_PAGE_COPY.emailPlaceholder}
              aria-label={LOGIN_PAGE_COPY.emailLabel}
              className="tv-login-page__field"
              onFocus={() => setEmailAutocompleteEnabled(true)}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              invalid={formik.touched.email && !!formik.errors.email}
            />
            {formik.touched.email && formik.errors.email ? (
              <TvSmall className="text-danger d-block mt-1">{formik.errors.email}</TvSmall>
            ) : null}
          </TvDiv>

          <TvDiv className="mb-4">
            <TvLabel htmlFor="login-password" className={AUTH_FIELD_LABEL_CLASS}>
              {LOGIN_PAGE_COPY.passwordLabel}
            </TvLabel>
            <TvDiv className="position-relative">
              <TvInput
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete={passwordAutocompleteEnabled ? "current-password" : "new-password"}
                readOnly={!passwordAutocompleteEnabled}
                placeholder={LOGIN_PAGE_COPY.passwordPlaceholder}
                aria-label={LOGIN_PAGE_COPY.passwordLabel}
                className="tv-login-page__field"
                onFocus={() => setPasswordAutocompleteEnabled(true)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                invalid={formik.touched.password && !!formik.errors.password}
              />
              <TvIconButton
                type="button"
                color="link"
                className="tv-login-page__password-toggle position-absolute top-50 end-0 translate-middle-y text-muted"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`mdi ${showPassword ? "mdi-eye-off" : "mdi-eye"} fs-5`} />
              </TvIconButton>
            </TvDiv>
            {formik.touched.password && formik.errors.password ? (
              <TvSmall className="text-danger d-block mt-1">{formik.errors.password}</TvSmall>
            ) : null}
          </TvDiv>

          <TvDiv className="d-grid mb-3">
            <TvButton
              color="primary"
              type="submit"
              disabled={loading}
              className={`tv-login-page__submit${!hasCredentials && !loading ? " disabled" : ""}`}
            >
              {loading ? <Spinner size="sm" color="light" /> : LOGIN_PAGE_COPY.signInButton}
            </TvButton>
          </TvDiv>
        </Form>
          ) : (
            <AuthContinueWithEmailButton onClick={() => setShowEmailForm(true)} />
          )}
          <TvDiv className="d-flex align-items-center justify-content-between">
            <Link to="/forgot-password" className="tv-login-page__text-link fw-semibold text-decoration-none">
              {LOGIN_PAGE_COPY.forgotPassword}
            </Link>
            <Link to="/register" className="tv-login-page__text-link fw-semibold text-decoration-none">
              {LOGIN_PAGE_COPY.createAccount}
            </Link>
          </TvDiv>
        </>
      ) : (
        <TvDiv data-testid="login-mfa-step" className="tv-login-page__form">
          <TvAlert color="info" className="mb-3" fade={false}>
            MFA required. Enter the code sent to your device.
          </TvAlert>
          {error ? (
            <TvAlert color="danger" className="mb-3" data-testid="login-error-alert" fade={false}>
              {error}
            </TvAlert>
          ) : null}
          <TvDiv className="mb-4">
            <TvLabel htmlFor="login-mfa-code" className={AUTH_FIELD_LABEL_CLASS}>
              {LOGIN_PAGE_COPY.mfaCodeLabel}
            </TvLabel>
            <TvInput
              id="login-mfa-code"
              type="text"
              placeholder={LOGIN_PAGE_COPY.mfaCodePlaceholder}
              aria-label={LOGIN_PAGE_COPY.mfaCodeLabel}
              className="tv-login-page__field"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              autoComplete="one-time-code"
            />
          </TvDiv>
          <TvDiv className="d-grid">
            <TvButton color="primary" onClick={handleMfaSubmit} disabled={loading} className="tv-login-page__submit">
              {loading ? <Spinner size="sm" color="light" /> : "Verify MFA"}
            </TvButton>
          </TvDiv>
        </TvDiv>
      )}
    </AuthSplitLayout>
  );
};

export default Login;
